import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Heart, Mail, CheckCircle, XCircle, Loader2, RefreshCw, ArrowRight } from 'lucide-react';
import apiClient from '../services/api';
import { useAuth } from '../context/AuthContext';

const EmailVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, fetchUser, login } = useAuth();
  
  // Support both token-based and OTP-based verification
  const token = searchParams.get('token');
  const code = searchParams.get('code');
  const email = searchParams.get('email');
  const returnTo = searchParams.get('returnTo') || localStorage.getItem('pendingRedirect');
  
  const [status, setStatus] = useState('pending'); // verifying, success, error, pending
  const [message, setMessage] = useState('');
  const [resending, setResending] = useState(false);
  const [otpInput, setOtpInput] = useState(code || '');
  const [verifying, setVerifying] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [pollCount, setPollCount] = useState(0);

  // Get the redirect destination after verification
  const getRedirectDestination = useCallback(() => {
    // Check for stored redirect
    const pendingRedirect = localStorage.getItem('pendingRedirect');
    if (pendingRedirect) {
      localStorage.removeItem('pendingRedirect');
      return pendingRedirect;
    }
    
    // Check URL param
    if (returnTo) {
      return returnTo;
    }
    
    // Check if user was creating a campaign
    const pendingCampaign = localStorage.getItem('pendingCampaign');
    if (pendingCampaign) {
      return '/create-campaign';
    }
    
    // Default to dashboard
    return '/dashboard';
  }, [returnTo]);

  // Auto-verify if code is in URL
  useEffect(() => {
    if (code && email) {
      setStatus('verifying');
      verifyWithOTP(code);
    } else if (token) {
      setStatus('verifying');
      verifyWithToken();
    }
  }, [code, email, token]);

  // Auto-poll to check verification status every 5 seconds
  useEffect(() => {
    if (status !== 'pending' || !user) return;
    
    const pollInterval = setInterval(async () => {
      try {
        // Refresh user data to check if email is verified
        const response = await apiClient.get('/auth/me');
        if (response.data.email_verified) {
          setStatus('success');
          setMessage('Email verified successfully!');
          clearInterval(pollInterval);
          
          // Redirect after short delay
          setTimeout(() => {
            const destination = getRedirectDestination();
            navigate(destination);
          }, 2000);
        }
        setPollCount(prev => prev + 1);
      } catch (error) {
        // Silently fail polling
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(pollInterval);
  }, [status, user, navigate, getRedirectDestination]);

  const verifyWithToken = async () => {
    try {
      const response = await apiClient.post('/auth/verify-email', { token });
      setStatus('success');
      setMessage(response.data.message || 'Email verified successfully!');
      
      // Refresh user data
      if (fetchUser) await fetchUser();
      
      // Redirect after short delay
      setTimeout(() => {
        const destination = getRedirectDestination();
        navigate(destination);
      }, 2000);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Verification failed. Please try again.');
    }
  };

  const verifyWithOTP = async (otpCode) => {
    setVerifying(true);
    try {
      let response;
      
      // If we have email from URL params, use public endpoint (no auth required)
      if (email) {
        response = await apiClient.post('/verification/verify-email-public', { 
          email: email,
          otp: otpCode 
        });
      } else {
        // If user is logged in, use authenticated endpoint
        response = await apiClient.post('/verification/email/verify-otp', { 
          otp: otpCode 
        });
      }
      
      if (response.data.verified || response.data.already_verified) {
        setStatus('success');
        setMessage(response.data.message || 'Email verified successfully!');
        
        // Refresh user data if logged in
        if (fetchUser) {
          try {
            await fetchUser();
          } catch (e) {
            // User might not be logged in
          }
        }
        
        // Redirect after short delay
        setTimeout(() => {
          const destination = getRedirectDestination();
          navigate(destination);
        }, 2000);
      } else {
        setStatus('error');
        setMessage('Invalid verification code. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Verification failed. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleOtpSubmit = (e) => {
    e.preventDefault();
    if (otpInput.length === 6) {
      setStatus('verifying');
      verifyWithOTP(otpInput);
    }
  };

  // Check verification status manually
  const checkVerificationStatus = async () => {
    setCheckingStatus(true);
    try {
      // Try to get user data to check if verified
      const response = await apiClient.get('/auth/me');
      if (response.data.email_verified) {
        setStatus('success');
        setMessage('Email verified successfully! Redirecting...');
        
        // Update local user data
        if (fetchUser) await fetchUser();
        
        // Redirect immediately to home page
        navigate('/');
      } else {
        setMessage('Email not verified yet. Please check your inbox and click the verification link.');
      }
    } catch (error) {
      // If not logged in, try to check via email
      if (email) {
        try {
          // Try to verify with a dummy code to get status
          setMessage('Please enter the verification code from your email or click the link in the email.');
        } catch (e) {
          setMessage('Unable to check status. Please try logging in.');
        }
      } else {
        setMessage('Please log in to check your verification status.');
      }
    } finally {
      setCheckingStatus(false);
    }
  };

  const resendVerification = async () => {
    setResending(true);
    try {
      const response = await apiClient.post('/verification/email/send-otp');
      
      // Check if email was actually sent
      if (response.data?.email_sent === false && response.data?.verification_link) {
        // Email service not configured, show direct link
        setMessage('Email service is not available. Click the link below to verify your email.');
        setVerificationLink(response.data.verification_link);
      } else {
        setMessage('Verification email sent! Please check your inbox.');
        setVerificationLink(null);
      }
      setStatus('pending');
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to resend. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const [verificationLink, setVerificationLink] = useState(null);

  // If user is already verified, redirect
  useEffect(() => {
    if (user?.email_verified && status !== 'verifying' && status !== 'success') {
      const destination = getRedirectDestination();
      navigate(destination);
    }
  }, [user, status, navigate, getRedirectDestination]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-emerald-500/5 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <Heart className="w-7 h-7 text-white" />
            </div>
            <span className="text-2xl font-bold text-foreground">FundTheHope</span>
          </Link>
        </div>

        <Card className="shadow-xl border-0 bg-card">
          {status === 'verifying' && (
            <CardContent className="pt-8 pb-8 text-center">
              <Loader2 className="w-16 h-16 mx-auto mb-4 text-emerald-500 animate-spin" />
              <CardTitle className="text-xl mb-2">Verifying your email...</CardTitle>
              <CardDescription>Please wait while we verify your email address.</CardDescription>
            </CardContent>
          )}

          {status === 'success' && (
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-emerald-500/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-emerald-500" />
              </div>
              <CardTitle className="text-xl mb-2 text-emerald-500">Email Verified!</CardTitle>
              <CardDescription className="mb-4">{message}</CardDescription>
              <p className="text-sm text-muted-foreground mb-4">Redirecting you now...</p>
              <div className="mt-6">
                <Button 
                  onClick={() => navigate(getRedirectDestination())} 
                  className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:opacity-90"
                >
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          )}

          {status === 'error' && (
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <CardTitle className="text-xl mb-2">Verification Failed</CardTitle>
              <CardDescription className="mb-6">{message}</CardDescription>
              
              {/* OTP Input for retry */}
              <form onSubmit={handleOtpSubmit} className="mb-6">
                <p className="text-sm text-muted-foreground mb-3">Enter the 6-digit code from your email:</p>
                <Input
                  type="text"
                  maxLength={6}
                  value={otpInput}
                  onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest mb-3"
                />
                <Button 
                  type="submit" 
                  disabled={otpInput.length !== 6 || verifying}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:opacity-90"
                >
                  {verifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Verify Code
                </Button>
              </form>
              
              <div className="space-y-3">
                <Button onClick={resendVerification} disabled={resending} variant="outline" className="w-full">
                  {resending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Resend Verification Email
                </Button>
                <Button variant="ghost" className="w-full" asChild>
                  <Link to="/login">Back to Login</Link>
                </Button>
              </div>
            </CardContent>
          )}

          {status === 'pending' && (
            <>
              <CardHeader className="text-center pb-2">
                <div className="w-20 h-20 mx-auto mb-4 bg-emerald-500/10 rounded-full flex items-center justify-center">
                  <Mail className="w-10 h-10 text-emerald-500" />
                </div>
                <CardTitle className="text-2xl">Verify Your Email</CardTitle>
                <CardDescription>
                  {email ? `We've sent a verification code to ${email}` : "We've sent a verification code to your email"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* OTP Input */}
                <form onSubmit={handleOtpSubmit}>
                  <p className="text-sm text-muted-foreground mb-3 text-center">Enter the 6-digit code:</p>
                  <Input
                    type="text"
                    maxLength={6}
                    value={otpInput}
                    onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className="text-center text-2xl tracking-widest mb-4"
                    autoFocus
                  />
                  <Button 
                    type="submit" 
                    disabled={otpInput.length !== 6 || verifying}
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:opacity-90"
                  >
                    {verifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Verify Email
                  </Button>
                </form>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">Or</span>
                  </div>
                </div>

                {/* Already verified button */}
                <Button 
                  onClick={checkVerificationStatus}
                  disabled={checkingStatus}
                  variant="outline" 
                  className="w-full border-emerald-500/30 hover:bg-emerald-500/10"
                >
                  {checkingStatus ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  I've Verified My Email
                </Button>

                <div className="bg-secondary/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground text-center">
                    Check your inbox for the verification email. Don't forget to check your spam folder!
                  </p>
                  {pollCount > 0 && (
                    <p className="text-xs text-center text-emerald-500 mt-2">
                      Auto-checking verification status...
                    </p>
                  )}
                </div>

                {message && (
                  <p className="text-sm text-center text-emerald-500">{message}</p>
                )}

                <div className="space-y-3">
                  <Button 
                    onClick={resendVerification} 
                    disabled={resending}
                    variant="outline" 
                    className="w-full"
                  >
                    {resending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Resend Verification Email
                  </Button>

                  <Button variant="ghost" className="w-full" asChild>
                    <Link to="/login">Back to Login</Link>
                  </Button>
                </div>

                <div className="text-center">
                  <p className="text-xs text-muted-foreground">
                    Need help? Contact{' '}
                    <a href="mailto:support@fundthehope.com" className="text-emerald-500 hover:underline">
                      support@fundthehope.com
                    </a>
                  </p>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default EmailVerificationPage;
